import { RanaModal, RanaTable } from "@nens/ranalib";

import { FoldersBreadcrumb } from "@/components/folders-breadcrumb";
import { TableEmptyFiles } from "@/components/table-empty-file";
import { useProjectFiles } from "@/hooks/use-project-files";
import { RanaFileIcon } from "@/lib/rana-file-data-type";
import { cancelButtonText, fileNameColumnString } from "@/translations";
import { ProjectFileOrDirectory } from "@/types/api-model-simple-names";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { MoveProjectFileOrDirectoryMutation } from "../api/move";

interface ProjectUserMoveModalProps {
  initialPaths: string[];
  items: ProjectFileOrDirectory[];
  onClose: (path?: string) => void;
  moveProjectFileOrDirectoryMutation: MoveProjectFileOrDirectoryMutation;
}

export function ProjectFileOrDirectoryMoveModal({
  initialPaths,
  items,
  onClose,
  moveProjectFileOrDirectoryMutation,
}: ProjectUserMoveModalProps) {
  const { isMutating, mutateWithConfirm } = moveProjectFileOrDirectoryMutation;
  const { paths, setPaths, path, intl, files, tableDataIds, clearFile, onDirectoryOrFileClick } =
    useProjectFiles({
      initialPaths,
    });

  const moveFileOrDirectory = () => {
    mutateWithConfirm(
      {
        items,
        destination: path,
      },
      {
        onSuccess: () => onClose(path),
      }
    );
  };

  const itemWithSameName = items.some((item) => tableDataIds.includes(item.id));

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={"Move files / folders"}
      maxW="xl"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: () => onClose(),
        },
        {
          title: itemWithSameName
            ? intl.formatMessage({
                id: "title.file_directory_already_existed",
                defaultMessage: "Some files / folder already exist in this folder",
              })
            : undefined,
          variant: "primary",
          isDisabled: itemWithSameName || isMutating,
          onClick: moveFileOrDirectory,
          children: intl.formatMessage({ id: "button.move", defaultMessage: "Move" }),
        },
      ]}
    >
      <FoldersBreadcrumb
        paths={paths}
        setPaths={(paths) => {
          setPaths(paths);
          clearFile();
        }}
      />
      <RanaTable
        columnDefinitions={[
          {
            width: "table_cell",
            title: fileNameColumnString(intl),
          },
        ]}
        tableDataIds={tableDataIds}
        emptyElement={<TableEmptyFiles />}
        isLoading={isMutating}
      >
        <RanaTable.Container mt={4}>
          <RanaTable.Header />
          <RanaTable.Body>
            {files.map((fileOrDirectory) => {
              const name = getNameFromFileId(fileOrDirectory.id);
              return (
                <RanaTable.Row
                  isDisabled={
                    fileOrDirectory.type === "file" ||
                    !!items.find((item) => item.id === fileOrDirectory.id)
                  }
                  onClick={() => onDirectoryOrFileClick(fileOrDirectory)}
                  key={fileOrDirectory.id}
                  rowId={fileOrDirectory.id}
                >
                  <RanaTable.Cell>
                    <RanaFileIcon fileOrDirectory={fileOrDirectory} />
                    {name}
                  </RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </RanaModal>
  );
}
